import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  Card,
  Button,
  RadioButton,
  Checkbox,
  Chip,
  useTheme,
  Surface,
  IconButton,
} from 'react-native-paper';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type {
  AssessmentQuestion,
  AssessmentOption,
  AssessmentResponse,
  BrewingScenario,
} from './types';

interface AssessmentQuestionProps {
  question: AssessmentQuestion;
  onResponse: (response: AssessmentResponse) => void;
  onSkip: () => void;
  allowSkip?: boolean;
  showConfidenceSlider?: boolean;
  style?: any;
}

interface ScenarioCardProps {
  scenario: BrewingScenario;
}

function ScenarioCard({ scenario }: ScenarioCardProps) {
  const theme = useTheme();

  return (
    <Card style={styles.scenarioCard} mode='outlined'>
      <Card.Content>
        <Text variant='titleMedium' style={styles.scenarioTitle}>
          {scenario.title}
        </Text>
        <Text
          variant='bodyMedium'
          style={[
            styles.scenarioDescription,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          {scenario.description}
        </Text>
        <Text
          variant='bodySmall'
          style={[
            styles.scenarioContext,
            { color: theme.colors.onSurfaceVariant },
          ]}
        >
          {scenario.context}
        </Text>

        <View style={styles.scenarioMeta}>
          <Chip
            icon='clock-outline'
            compact
            style={styles.metaChip}
            textStyle={styles.metaChipText}
          >
            {scenario.timeRequired} min
          </Chip>
          <Chip
            icon='chart-line'
            compact
            style={styles.metaChip}
            textStyle={styles.metaChipText}
          >
            Difficulty: {scenario.difficulty}/10
          </Chip>
        </View>

        <View style={styles.equipmentList}>
          <Text variant='bodySmall' style={styles.equipmentTitle}>
            Equipment needed:
          </Text>
          <View style={styles.equipmentChips}>
            {scenario.equipment.map((item, index) => (
              <Chip
                key={index}
                compact
                style={styles.equipmentChip}
                textStyle={styles.equipmentChipText}
              >
                {item}
              </Chip>
            ))}
          </View>
        </View>
      </Card.Content>
    </Card>
  );
}

export function AssessmentQuestion({
  question,
  onResponse,
  onSkip,
  allowSkip = true,
  showConfidenceSlider = true,
  style,
}: AssessmentQuestionProps) {
  const theme = useTheme();
  const [selectedValues, setSelectedValues] = useState<any[]>([]);
  const [confidence, setConfidence] = useState(0.7);
  const [startTime] = useState(Date.now());

  const isMultipleChoice =
    question.type === 'multiple_choice' || question.type === 'preference';
  const allowMultipleSelection =
    question.type === 'preference' &&
    (question.id === 'brewing_methods' || question.id === 'learning_goals');

  useEffect(() => {
    // Reset state when question changes
    setSelectedValues([]);
    setConfidence(0.7);
  }, [question.id]);

  const handleOptionSelect = (option: AssessmentOption) => {
    if (allowMultipleSelection) {
      setSelectedValues(prev => {
        const isSelected = prev.some(val => val.id === option.id);
        if (isSelected) {
          return prev.filter(val => val.id !== option.id);
        } else {
          return [...prev, option];
        }
      });
    } else {
      setSelectedValues([option]);
    }
  };

  const handleSubmit = () => {
    const timeSpent = (Date.now() - startTime) / 1000; // in seconds
    const response: AssessmentResponse = {
      questionId: question.id,
      questionType: question.type,
      response: allowMultipleSelection ? selectedValues : selectedValues[0],
      confidence,
      timeSpent,
      timestamp: new Date().toISOString(),
    };
    onResponse(response);
  };

  const handleSkip = () => {
    const timeSpent = (Date.now() - startTime) / 1000;
    const response: AssessmentResponse = {
      questionId: question.id,
      questionType: question.type,
      response: null,
      confidence: 0,
      timeSpent,
      timestamp: new Date().toISOString(),
    };
    onResponse(response);
    onSkip();
  };

  const canSubmit = selectedValues.length > 0;
  const meetsConfidenceThreshold =
    !question.requiredConfidence || confidence >= question.requiredConfidence;

  return (
    <ScrollView
      style={[styles.container, style]}
      showsVerticalScrollIndicator={false}
    >
      <Card style={styles.questionCard} mode='outlined'>
        <Card.Content>
          {/* Question Header */}
          <View style={styles.questionHeader}>
            <Text variant='headlineSmall' style={styles.questionText}>
              {question.question}
            </Text>
            {question.description && (
              <Text
                variant='bodyMedium'
                style={[
                  styles.questionDescription,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                {question.description}
              </Text>
            )}
          </View>

          {/* Scenario Display */}
          {question.scenario && <ScenarioCard scenario={question.scenario} />}

          {/* Options */}
          <View style={styles.optionsContainer}>
            {question.options?.map(option => {
              const isSelected = selectedValues.some(
                val => val.id === option.id
              );

              return (
                <Surface
                  key={option.id}
                  style={[
                    styles.optionCard,
                    isSelected && {
                      backgroundColor: theme.colors.primaryContainer,
                      borderColor: theme.colors.primary,
                      borderWidth: 2,
                    },
                  ]}
                  elevation={isSelected ? 2 : 1}
                >
                  <View style={styles.optionContent}>
                    <View style={styles.optionHeader}>
                      {allowMultipleSelection ? (
                        <Checkbox
                          status={isSelected ? 'checked' : 'unchecked'}
                          onPress={() => handleOptionSelect(option)}
                        />
                      ) : (
                        <RadioButton
                          value={option.id}
                          status={isSelected ? 'checked' : 'unchecked'}
                          onPress={() => handleOptionSelect(option)}
                        />
                      )}

                      <View style={styles.optionTextContainer}>
                        <View style={styles.optionTitleRow}>
                          {option.icon && (
                            <Text style={styles.optionIcon}>{option.icon}</Text>
                          )}
                          <Text
                            variant='bodyLarge'
                            style={[
                              styles.optionText,
                              isSelected && {
                                color: theme.colors.onPrimaryContainer,
                              },
                            ]}
                          >
                            {option.text}
                          </Text>
                        </View>

                        {option.description && (
                          <Text
                            variant='bodySmall'
                            style={[
                              styles.optionDescription,
                              {
                                color: isSelected
                                  ? theme.colors.onPrimaryContainer
                                  : theme.colors.onSurfaceVariant,
                              },
                            ]}
                          >
                            {option.description}
                          </Text>
                        )}
                      </View>
                    </View>
                  </View>
                </Surface>
              );
            })}
          </View>

          {/* Confidence Slider */}
          {showConfidenceSlider && canSubmit && (
            <View style={styles.confidenceSection}>
              <Text variant='bodyMedium' style={styles.confidenceTitle}>
                How confident are you in your response?
              </Text>
              <ConfidenceIndicator
                confidence={confidence}
                showLabel={true}
                showDescription={true}
                size='medium'
              />
              <View style={styles.confidenceControls}>
                <IconButton
                  icon='minus'
                  size={20}
                  onPress={() => setConfidence(Math.max(0.1, confidence - 0.1))}
                />
                <View style={styles.confidenceSlider}>
                  {[0.2, 0.4, 0.6, 0.8, 1.0].map(value => (
                    <Button
                      key={value}
                      mode={
                        Math.abs(confidence - value) < 0.05
                          ? 'contained'
                          : 'outlined'
                      }
                      compact
                      onPress={() => setConfidence(value)}
                      style={styles.confidenceButton}
                    >
                      {Math.round(value * 100)}%
                    </Button>
                  ))}
                </View>
                <IconButton
                  icon='plus'
                  size={20}
                  onPress={() => setConfidence(Math.min(1.0, confidence + 0.1))}
                />
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionButtons}>
            {allowSkip && (
              <Button
                mode='outlined'
                onPress={handleSkip}
                style={styles.skipButton}
              >
                Skip
              </Button>
            )}
            <Button
              mode='contained'
              onPress={handleSubmit}
              disabled={!canSubmit || !meetsConfidenceThreshold}
              style={styles.submitButton}
            >
              {allowMultipleSelection && selectedValues.length > 1
                ? `Continue (${selectedValues.length} selected)`
                : 'Continue'}
            </Button>
          </View>

          {/* Confidence Warning */}
          {question.requiredConfidence &&
            !meetsConfidenceThreshold &&
            canSubmit && (
              <Text
                variant='bodySmall'
                style={[
                  styles.confidenceWarning,
                  { color: theme.colors.error },
                ]}
              >
                Please increase your confidence level to at least{' '}
                {Math.round(question.requiredConfidence * 100)}% to continue.
              </Text>
            )}
        </Card.Content>
      </Card>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  questionCard: {
    margin: 16,
  },
  questionHeader: {
    marginBottom: 16,
  },
  questionText: {
    fontWeight: '600',
    marginBottom: 8,
  },
  questionDescription: {
    lineHeight: 20,
  },
  scenarioCard: {
    marginBottom: 16,
  },
  scenarioTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  scenarioDescription: {
    marginBottom: 8,
    lineHeight: 20,
  },
  scenarioContext: {
    fontStyle: 'italic',
    marginBottom: 12,
    lineHeight: 18,
  },
  scenarioMeta: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  metaChip: {
    marginRight: 8,
  },
  metaChipText: {
    fontSize: 12,
  },
  equipmentList: {
    marginTop: 8,
  },
  equipmentTitle: {
    fontWeight: '500',
    marginBottom: 4,
  },
  equipmentChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  equipmentChip: {
    marginRight: 4,
    marginBottom: 4,
  },
  equipmentChipText: {
    fontSize: 11,
  },
  optionsContainer: {
    marginBottom: 16,
  },
  optionCard: {
    marginBottom: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  optionContent: {
    padding: 12,
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  optionTextContainer: {
    flex: 1,
    marginLeft: 8,
  },
  optionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  optionIcon: {
    fontSize: 18,
    marginRight: 8,
  },
  optionText: {
    fontWeight: '500',
    flex: 1,
  },
  optionDescription: {
    lineHeight: 16,
  },
  confidenceSection: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: 'rgba(0,0,0,0.02)',
    borderRadius: 8,
  },
  confidenceTitle: {
    fontWeight: '500',
    marginBottom: 8,
  },
  confidenceControls: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  confidenceSlider: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginHorizontal: 8,
  },
  confidenceButton: {
    minWidth: 50,
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  skipButton: {
    flex: 0.4,
  },
  submitButton: {
    flex: 0.55,
  },
  confidenceWarning: {
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
});
